import { useCallback, useEffect, useRef, useState } from 'react';

interface UseIsolatedSearchOptions {
  debounceMs?: number;
  onSearch?: (searchTerm: string) => void;
}

export function useIsolatedSearch<T>({
  debounceMs = 300,
  onSearch
}: UseIsolatedSearchOptions) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  const debounceTimeoutRef = useRef<NodeJS.Timeout>();

  // Função de busca com debounce isolado
  const debouncedSearch = useCallback(
    (term: string) => {
      // Limpar timeout anterior
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      setIsSearching(true);

      // Aplicar debounce
      debounceTimeoutRef.current = setTimeout(() => {
        if (onSearch) {
          onSearch(term);
        }
        setIsSearching(false);
      }, debounceMs);
    },
    [onSearch, debounceMs]
  );

  // Função para iniciar busca
  const search = useCallback(
    (term: string) => {
      setSearchTerm(term);

      // Se é a primeira busca e tem onSearch, carregar dados iniciais
      if (!hasInitialLoad && onSearch) {
        setHasInitialLoad(true);
        debouncedSearch(term);
      } else if (onSearch && term.length > 0) {
        // Busca com filtro apenas se há termo de busca
        debouncedSearch(term);
      }
    },
    [hasInitialLoad, onSearch, debouncedSearch]
  );

  // Função para busca inicial (carregamento)
  const loadInitialData = useCallback(() => {
    if (!hasInitialLoad && onSearch) {
      setHasInitialLoad(true);
      setIsSearching(true);
      debouncedSearch('');
    }
  }, [hasInitialLoad, onSearch, debouncedSearch]);

  // Função de filtro local
  const filterLocalData = useCallback((data: T[], term: string): T[] => {
    if (!term.trim()) {
      return data.slice(0, 50);
    } // Limitar para performance

    return data
      .filter((item: unknown) => {
        const typedItem = item as Record<string, unknown>;
        const searchLower = term.toLowerCase();
        // Tentar buscar em propriedades comuns como name, label, etc.
        const searchableFields = [
          typedItem.name,
          typedItem.label,
          typedItem.title,
          typedItem.value,
          typedItem.id,
          typedItem.wdId
        ].filter(Boolean);

        return searchableFields.some(field =>
          String(field).toLowerCase().includes(searchLower)
        );
      })
      .slice(0, 20); // Limitar resultados
  }, []);

  // Cleanup
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return {
    searchTerm,
    isSearching,
    hasInitialLoad,
    search,
    loadInitialData,
    filterLocalData,
    setSearchTerm
  };
}
